import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';
import { updateUserTitle } from '../graphql/mutations';
import { automatedBadgeService } from './automatedBadgeService';
import awsconfig from '../aws-exports';

// Direct GraphQL fetch for public access using API key
const directGraphQLFetch = async (query, variables) => {
  console.log('🔑 Direct GraphQL fetch:', {
    endpoint: awsconfig.aws_appsync_graphqlEndpoint,
    apiKey: awsconfig.aws_appsync_apiKey?.substring(0, 10) + '...',
    variables
  });
  
  const response = await fetch(awsconfig.aws_appsync_graphqlEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': awsconfig.aws_appsync_apiKey
    },
    body: JSON.stringify({ query, variables })
  });
  
  console.log('🔑 Response status:', response.status, response.statusText);
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error('🔑 Response error:', errorText);
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  const result = await response.json();
  console.log('🔑 Response data:', result);
  return result;
};

// Lazy client initialization to ensure Amplify is configured first
let client = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};

// GraphQL queries and mutations for achievements
const GET_USER_ACHIEVEMENTS = `
  query ListUserAchievements($filter: ModelUserAchievementFilterInput) {
    listUserAchievements(filter: $filter) {
      items {
        id
        userID
        achievementType
        unlocked
        unlockedAt
        progress
        metadata
        createdAt
        updatedAt
      }
    }
  }
`;

const GET_USER_BADGES = `
  query GetUserBadges($userID: ID!) {
    listUserBadges(filter: { userID: { eq: $userID } }) {
      items {
        id
        userID
        badgeType
        earnedAt
        metadata
      }
    }
  }
`;

const CREATE_ACHIEVEMENT = `
  mutation CreateUserAchievement($input: CreateUserAchievementInput!) {
    createUserAchievement(input: $input) {
      id
      userID
      achievementType
      unlocked
      unlockedAt
      progress
    }
  }
`;

const UPDATE_ACHIEVEMENT = `
  mutation UpdateUserAchievement($input: UpdateUserAchievementInput!) {
    updateUserAchievement(input: $input) {
      id
      progress
      unlocked
      unlockedAt
    }
  }
`;

export const achievementService = {
  // Get user achievements
  async getUserAchievements(userId) {
    try {
      if (!userId) {
        console.error('getUserAchievements: userId is required');
        return [];
      }
      
      console.log('🔍 getUserAchievements called for userId:', userId);
      console.log('🔍 Filter:', { userID: { eq: userId }, unlocked: { eq: true } });
      
      const result = await getClient().graphql({
        query: GET_USER_ACHIEVEMENTS,
        variables: { 
          filter: { 
            userID: { eq: userId },
            unlocked: { eq: true }
          } 
        }
      });
      
      console.log('📊 Raw GraphQL result:', JSON.stringify(result, null, 2));
      
      const items = result.data?.listUserAchievements?.items || [];
      console.log(`✅ Found ${items.length} achievements for user ${userId}`);
      
      if (items.length > 0) {
        console.log('🏆 Achievement types:', items.map(i => i.achievementType));
        console.log('🏆 Unlocked status:', items.map(i => i.unlocked));
      }
      
      // Filter out items with null required fields
      const filtered = items.filter(item => 
        item && 
        item.userID && 
        item.achievementType && 
        item.unlocked !== null && 
        item.createdAt
      );
      
      console.log(`✅ After filtering: ${filtered.length} valid achievements`);
      
      return filtered;
    } catch (error) {
      console.error('❌ Error fetching achievements:', error);
      if (error.errors) {
        error.errors.forEach((err, index) => {
          console.error(`GraphQL Error ${index + 1}:`, err.message);
          console.error('Error path:', err.path);
          console.error('Error extensions:', err.extensions);
        });
      }
      return [];
    }
  },

  // Get user badges
  async getUserBadges(userId) {
    try {
      if (!userId) {
        console.error('getUserBadges: userId is required');
        return [];
      }
      
      const LIST_BADGE_DEFINITIONS = `
        query ListBadgeDefinitions {
          listBadgeDefinitions {
            items {
              id
              type
              name
              description
              icon
              color
              rarity
              points
            }
          }
        }
      `;
      
      const [badgeResult, defsResult] = await Promise.all([
        getClient().graphql({
          query: GET_USER_BADGES,
          variables: { userID: userId }
        }),
        getClient().graphql({ query: LIST_BADGE_DEFINITIONS })
      ]);
      
      const userBadges = badgeResult.data?.listUserBadges?.items || [];
      const badgeDefinitions = defsResult.data?.listBadgeDefinitions?.items || [];
      
      // Enhance badges with definition data
      const enhancedBadges = userBadges.map(badge => {
        const definition = badgeDefinitions.find(def => def.type === badge.badgeType);
        return {
          ...badge,
          name: definition?.name || badge.badgeType,
          description: definition?.description || 'User badge',
          icon: definition?.icon || 'Star',
          color: definition?.color || '#1976d2',
          rarity: definition?.rarity || 'common',
          points: definition?.points || 0
        };
      });
      
      return enhancedBadges;
    } catch (error) {
      console.error('Error fetching badges:', error);
      if (error.errors) {
        error.errors.forEach((err, index) => {
          console.error(`GraphQL Error ${index + 1}:`, err.message);
        });
      }
      return [];
    }
  },

  // Get user stats for achievement calculation
  async getUserStats(userId) {
    try {
      const GET_USER_STATS = `
        query GetUser($id: ID!) {
          getUser(id: $id) {
            followersCount
            reviewsCount
            moviesWatched
            loginStreak
            earlyDiscoveries
            totalAchievementPoints
            leaderboardPoints
            currentLeaderboardPeriod
            titles
          }
        }
      `;
      
      const result = await getClient().graphql({
        query: GET_USER_STATS,
        variables: { id: userId }
      });
      
      const userStats = result.data?.getUser || {};
      
      // Calculate distinct movies watched count
      const distinctMoviesWatched = await this.getDistinctMoviesWatchedCount(userId);
      
      return {
        ...userStats,
        moviesWatched: distinctMoviesWatched
      };
    } catch (error) {
      console.error('Error fetching user stats:', error);
      return {};
    }
  },

  // Get distinct movies watched count
  async getDistinctMoviesWatchedCount(userId) {
    try {
      const GET_WATCHED_MOVIES = `
        query ListUserMovieInteractions($filter: ModelUserMovieInteractionFilterInput) {
          listUserMovieInteractions(filter: $filter) {
            items {
              movieID
            }
          }
        }
      `;
      
      const result = await getClient().graphql({
        query: GET_WATCHED_MOVIES,
        variables: {
          filter: {
            userID: { eq: userId },
            watched: { eq: true }
          }
        }
      });
      
      const watchedMovies = result.data?.listUserMovieInteractions?.items || [];
      const distinctMovieIds = [...new Set(watchedMovies.map(item => item.movieID))];
      
      return distinctMovieIds.length;
    } catch (error) {
      console.error('Error fetching distinct movies watched:', error);
      return 0;
    }
  },

  // Check and unlock achievements based on user action
  async checkAchievements(userId, actionType, metadata = {}) {
    try {
      const CALCULATE_ACHIEVEMENT_PROGRESS = `
        mutation CalculateAchievementProgress($input: CalculateAchievementProgressInput!) {
          calculateAchievementProgress(input: $input) {
            success
            userId
            newlyUnlocked {
              id
              achievementType
              achievementName
              unlocked
              unlockedAt
            }
            multiplierApplied
            timestamp
          }
        }
      `;
      
      const result = await getClient().graphql({
        query: CALCULATE_ACHIEVEMENT_PROGRESS,
        variables: {
          input: { 
            userId: userId, 
            actionType, 
            metadata: typeof metadata === 'string' ? metadata : JSON.stringify(metadata || {}) 
          }
        }
      });
      
      const response = result.data?.calculateAchievementProgress;
      if (response?.success && response.newlyUnlocked?.length > 0) {
        // Trigger notifications for newly unlocked achievements
        response.newlyUnlocked.forEach(achievement => {
          this.triggerAchievementNotification(achievement);
        });
        
        // Auto-update user title after achievement changes
        await this.updateUserTitle(userId);
        
        // Check for automated badge eligibility
        await automatedBadgeService.checkUserForBadges(userId);
        
        return response.newlyUnlocked || [];
      }
      return [];
    } catch (error) {
      console.error('Error checking achievements:', error);
      if (error.errors) {
        error.errors.forEach((err, index) => {
          console.error(`GraphQL Error ${index + 1}:`, err.message);
          console.error('Error path:', err.path);
          console.error('Error extensions:', err.extensions);
        });
      }
      return [];
    }
  },

  // Trigger achievement notification
  async triggerAchievementNotification(achievement) {
    console.log('Achievement unlocked:', achievement.achievementName || achievement.achievementType);
    
    // Format achievement for premium notification
    const achievementData = {
      name: achievement.achievementName || achievement.achievementType,
      description: achievement.description || `You've unlocked: ${achievement.achievementName || achievement.achievementType}!`,
      points: achievement.points || 100,
      rarity: achievement.rarity || 'common',
      category: achievement.category || 'general'
    };

    // Dispatch custom event for notification system
    const event = new CustomEvent('achievementUnlocked', { detail: achievementData });
    window.dispatchEvent(event);
  },

  // Update user title based on achievements (respects admin assignments)
  async updateUserTitle(userId) {
    try {
      // Check if user has admin-assigned title that should never be overridden
      const GET_USER_TITLE_INFO = `
        query GetUser($id: ID!) {
          getUser(id: $id) {
            id
            titles
            titleSource
          }
        }
      `;
      
      const userResult = await getClient().graphql({
        query: GET_USER_TITLE_INFO,
        variables: { id: userId }
      });
      
      const user = userResult.data?.getUser;
      
      // If user has admin-assigned title, NEVER override it
      if (user?.titleSource === 'ADMIN') {
        console.log('Skipping auto-title update - admin-assigned title is permanent');
        return { success: true, titleChanged: false, reason: 'Admin title is permanent' };
      }
      
      const result = await getClient().graphql({
        query: updateUserTitle,
        variables: { input: { userId: userId } }
      });
      
      const response = result.data?.updateUserTitle;
      
      // If title changed, trigger notification
      if (response?.success && response.titleChanged) {
        const titleData = {
          name: response.titleName || response.newTitle,
          description: response.titleDescription || `You are now ${response.titleName || response.newTitle}!`,
          color: response.titleColor || '#1976d2',
          previousTitle: response.previousTitle,
          newTitle: response.newTitle,
          achievementCount: response.achievementCount,
          totalPoints: response.totalPoints
        };

        // Dispatch custom event for title update notification
        const event = new CustomEvent('titleUpdated', { detail: titleData });
        window.dispatchEvent(event);
      }
      
      return response;
    } catch (error) {
      console.error('Error updating user title:', error);
      return null;
    }
  },

  // Get achievement leaderboard
  async getLeaderboard(achievementType = null, timeframe = 'ALL_TIME', limit = 10) {
    console.log('🏆 getLeaderboard called with:', { achievementType, timeframe, limit });
    
    try {
      const GET_ACHIEVEMENT_LEADERBOARD = `
        query GetAchievementLeaderboard($input: GetAchievementLeaderboardInput!) {
          getAchievementLeaderboard(input: $input) {
            success
            leaderboard {
              rank
              userId
              username
              displayName
              avatar
              title
              achievementCount
              totalPoints
              leaderboardPoints
              currentPeriod
              badgeCount
              latestUnlock
            }
            totalEntries
            timeframe
            generatedAt
          }
        }
      `;
      
      // First try authenticated request if user is logged in
      try {
        const user = await getCurrentUser();
        if (user) {
          console.log('🏆 User authenticated, trying userPool auth');
          const result = await getClient().graphql({
            query: GET_ACHIEVEMENT_LEADERBOARD,
            variables: {
              input: { achievementType, timeframe, limit }
            },
            authMode: 'userPool'
          });
          console.log('🏆 Authenticated result:', result);
          const response = result.data?.getAchievementLeaderboard;
          console.log('🏆 Authenticated response:', response);
          console.log('🏆 Sample leaderboard entry:', response?.leaderboard?.[0]);
          return response?.success ? response.leaderboard : [];
        }
      } catch (authError) {
        console.log('🏆 Auth failed, trying public access:', authError.message);
      }
      
      // Use direct API key access for public/anonymous users
      console.log('🏆 Using direct API key access');
      const result = await directGraphQLFetch(GET_ACHIEVEMENT_LEADERBOARD, {
        input: { achievementType, timeframe, limit }
      });
      
      console.log('🏆 Direct API result:', result);
      const response = result.data?.getAchievementLeaderboard;
      console.log('🏆 Direct API response:', response);
      
      if (result.errors) {
        console.error('🏆 GraphQL errors:', result.errors);
        throw new Error(`GraphQL errors: ${result.errors.map(e => e.message).join(', ')}`);
      }
      
      const leaderboard = response?.success ? response.leaderboard : [];
      console.log('🏆 Final leaderboard data:', leaderboard);
      
      // Enhance leaderboard with user details and badge count
      console.log('🏆 Starting enhancement...');
      const enhancedLeaderboard = await this.enhanceLeaderboardWithUserDetails(leaderboard);
      console.log('🏆 Enhancement complete');
      return enhancedLeaderboard;
    } catch (error) {
      console.error('🏆 Error fetching leaderboard:', error);
      return [];
    }
  },

  // Share achievement as post
  async shareAchievement(userId, achievementId, shareToFeed = true) {
    try {
      // First get the achievement details
      const GET_ACHIEVEMENT = `
        query GetUserAchievement($id: ID!) {
          getUserAchievement(id: $id) {
            id
            userID
            achievementType
            achievementName
            description
            unlockedAt
            progress
          }
        }
      `;
      
      const achievementResult = await getClient().graphql({
        query: GET_ACHIEVEMENT,
        variables: { id: achievementId }
      });
      
      const achievement = achievementResult.data?.getUserAchievement;
      if (!achievement) {
        throw new Error('Achievement not found');
      }
      
      // Create a social media post about the achievement
      const CREATE_POST = `
        mutation CreatePost($input: CreatePostInput!) {
          createPost(input: $input) {
            id
            authorID
            content
            type
            createdAt
          }
        }
      `;
      
      const postContent = `🏆 Achievement Unlocked! 🎉\n\n${achievement.achievementName}\n\n${achievement.description || 'Another milestone reached!'}\n\n#Achievement #HypeCircle #Milestone`;
      
      const postResult = await getClient().graphql({
        query: CREATE_POST,
        variables: {
          input: {
            authorID: userId,
            content: postContent,
            type: 'achievement_share',
            metadata: JSON.stringify({
              achievementId: achievementId,
              achievementType: achievement.achievementType,
              achievementName: achievement.achievementName,
              unlockedAt: achievement.unlockedAt
            })
          }
        }
      });
      
      return {
        success: true,
        post: postResult.data?.createPost,
        sharedToFeed: true,
        socialPostCreated: true,
        message: 'Achievement shared successfully!'
      };
    } catch (error) {
      console.error('Error sharing achievement:', error);
      if (error.errors) {
        error.errors.forEach((err, index) => {
          console.error(`GraphQL Error ${index + 1}:`, err.message);
          console.error('Error path:', err.path);
          console.error('Error extensions:', err.extensions);
        });
      }
      throw error;
    }
  },

  // Enhance leaderboard with user details and badge count
  async enhanceLeaderboardWithUserDetails(leaderboard) {
    console.log('🏆 enhanceLeaderboardWithUserDetails called with:', leaderboard.length, 'entries');
    
    if (!leaderboard || leaderboard.length === 0) {
      console.log('🏆 No leaderboard data to enhance');
      return leaderboard;
    }
    
    try {
      const GET_USER_DETAILS = `
        query GetUser($id: ID!) {
          getUser(id: $id) {
            id
            username
            displayName
            avatar
            titles
            activeBadges
          }
        }
      `;
      
      console.log('🏆 Processing', leaderboard.length, 'users...');
      
      const enhancedEntries = await Promise.all(
        leaderboard.map(async (entry, index) => {
          try {
            console.log(`🏆 Processing user ${index + 1}/${leaderboard.length}: ${entry.userId}`);
            
            // Get user details
            const userResult = await getClient().graphql({
              query: GET_USER_DETAILS,
              variables: { id: entry.userId }
            });
            
            const userDetails = userResult.data?.getUser;
            console.log(`🏆 User ${entry.userId} details:`, userDetails);
            
            // Get user badges count
            const badges = await this.getUserBadges(entry.userId);
            console.log(`🏆 User ${entry.userId} badges:`, badges.length);
            
            const enhanced = {
              ...entry,
              displayName: userDetails?.displayName || entry.displayName,
              avatar: userDetails?.avatar || entry.avatar,
              title: userDetails?.titles?.[0] || entry.title,
              badgeCount: badges.length
            };
            
            console.log(`🏆 Enhanced user ${entry.userId}:`, enhanced);
            return enhanced;
          } catch (error) {
            console.error(`Error enhancing user ${entry.userId}:`, error);
            return {
              ...entry,
              badgeCount: 0
            };
          }
        })
      );
      
      console.log('🏆 All users processed. Enhanced leaderboard:', enhancedEntries);
      return enhancedEntries;
    } catch (error) {
      console.error('Error enhancing leaderboard:', error);
      return leaderboard;
    }
  },

  // Calculate achievement progress
  calculateProgress(achievementType, userStats) {
    const progressMap = {
      FIRST_FOLLOWER: Math.min(userStats.followersCount || 0, 1),
      POPULAR_USER: Math.min((userStats.followersCount || 0) / 100, 1),
      INFLUENCER: Math.min((userStats.followersCount || 0) / 1000, 1),
      FIRST_REVIEW: Math.min(userStats.reviewsCount || 0, 1),
      PROLIFIC_REVIEWER: Math.min((userStats.reviewsCount || 0) / 50, 1),
      CENTURY_CLUB: Math.min((userStats.moviesWatched || 0) / 100, 1),
      MOVIE_MARATHON: Math.min((userStats.moviesWatched || 0) / 500, 1),
      DAILY_VISITOR: Math.min((userStats.loginStreak || 0) / 7, 1),
      DEDICATION: Math.min((userStats.loginStreak || 0) / 30, 1)
    };

    return Math.floor((progressMap[achievementType] || 0) * 100);
  },

  // Recalculate user achievements
  async recalculateUserAchievements(userId) {
    try {
      console.log('Recalculating achievements for user:', userId);
      
      // Update user's movies watched count with distinct count
      await this.updateUserMoviesWatchedCount(userId);
      
      // Trigger achievement recalculation
      const result = await this.checkAchievements(userId, 'recalculate_all', {
        trigger: 'manual_recalculation',
        timestamp: new Date().toISOString()
      });
      
      console.log('Achievement recalculation completed:', result);
      return result;
    } catch (error) {
      console.error('Error recalculating achievements:', error);
      throw error;
    }
  },

  // Update user's movies watched count with distinct count
  async updateUserMoviesWatchedCount(userId) {
    try {
      const distinctCount = await this.getDistinctMoviesWatchedCount(userId);
      
      const UPDATE_USER_MOVIES_COUNT = `
        mutation UpdateUser($input: UpdateUserInput!) {
          updateUser(input: $input) {
            id
            moviesWatched
          }
        }
      `;
      
      await getClient().graphql({
        query: UPDATE_USER_MOVIES_COUNT,
        variables: {
          input: {
            id: userId,
            moviesWatched: distinctCount
          }
        }
      });
      
      console.log(`Updated user ${userId} movies watched count to ${distinctCount}`);
      return distinctCount;
    } catch (error) {
      console.error('Error updating user movies watched count:', error);
      return 0;
    }
  }
};